import { Menu as HeadlessMenu, Transition } from "@headlessui/react";
import React, { JSX, ReactNode } from "react";
import { cn } from "ui/lib/utils";

const { Button, Items, Item } = HeadlessMenu;

interface MenuTransitionProps {
  children: ReactNode;
  show?: boolean | undefined;
  appear?: boolean | undefined;
}

export const MenuTransition: React.FC<MenuTransitionProps> = ({
  children,
  ...props
}) => {
  return (
    <Transition
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
      {...props}
    >
      {children}
    </Transition>
  );
};

interface Option {
  value?: string;
  label?: string;
  disabled?: boolean;
  dangerous?: boolean;
  divider?: boolean;
  preventDefault?: boolean;
  description?: string;
  render?: (props: any) => JSX.Element;
  renderAction?: () => JSX.Element;
}

export interface MenuProps {
  children: ReactNode;
  options: Option[];
  currentlySelectedValues?: string[];
  onChange: (selectedItem: Option) => void;
  onRemove?: (selectedItem: Option) => void;
  buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  style?: React.CSSProperties;
  disabled?: boolean;
  align?: string;
  width?: string;
  itemClassnames?: string;
  menuZIndex?: string;
}

const Menu: React.FC<MenuProps> = React.memo(
  ({
    children,
    options = [],
    currentlySelectedValues,
    onChange,
    onRemove,
    buttonProps,
    style = {},
    disabled,
    align = "right",
    width = "56",
    itemClassnames = "",
    menuZIndex = "100",
  }) => {
    const handleChange = (selectedItem: Option) => {
      if (selectedItem.preventDefault) return;
      const isSelected = currentlySelectedValues?.includes?.(
        selectedItem.value as string
      );

      if (isSelected && typeof onRemove === "function") {
        return onRemove(selectedItem);
      }
      onChange(selectedItem);
    };

    return (
      <HeadlessMenu>
        {({ open }) => (
          <div
            style={style}
            className={cn("relative max-w-full", {
              "select-none opacity-85": disabled,
            })}
          >
            <Button disabled={disabled} type="button" {...buttonProps}>
              {children}
            </Button>
            <span className="relative z-40 block">
              <MenuTransition show={open && !disabled}>
                <Items
                  static={false}
                  className={`absolute ${align}-0 w-${width} mt-2 px-1 origin-top-${align} bg-background border border rounded-md shadow-lg outline-none z-${menuZIndex}`}
                >
                  <div className="flex flex-col w-full py-1">
                    {options
                      ?.filter?.((option) => !!option?.value)
                      ?.map?.((option, ndx) => {
                        if (option.divider)
                          return (
                            <hr
                              className="border border-neutral-700"
                              key={option.value || ndx}
                            />
                          );
                        return (
                          <Item
                            key={option.value || ndx}
                            disabled={option.disabled}
                          >
                            {({ active }) => (
                              <button
                                onClick={() => handleChange(option)}
                                className={cn(
                                  `w-full group px-3 py-1 my-1 rounded leading-5 text-sm text-left hover:text-space-900 ${itemClassnames}`,
                                  {
                                    "text-vermillion bg-transparent hover:bg-primary":
                                      option?.dangerous,
                                    "opacity-50 cursor-not-allowed":
                                      option.disabled,
                                    "bg-primary text-space-900 shadow-button":
                                      active && !option?.dangerous,
                                    "text-neutral-50 bg-transparent hover:bg-primary":
                                      !active && !option?.dangerous,
                                  }
                                )}
                              >
                                <div className="flex justify-between w-full">
                                  {typeof option.render === "function" ? (
                                    option.render({ handleChange, active })
                                  ) : (
                                    <span className="w-full">
                                      {option.label}
                                    </span>
                                  )}
                                  {typeof option.renderAction === "function"
                                    ? option.renderAction()
                                    : null}
                                </div>
                                {typeof option.description === "string" &&
                                option.description ? (
                                  <span className="text-xs text-muted-foreground group-hover:text-neutral-700">
                                    {option.description}
                                  </span>
                                ) : null}
                              </button>
                            )}
                          </Item>
                        );
                      })}
                  </div>
                </Items>
              </MenuTransition>
            </span>
          </div>
        )}
      </HeadlessMenu>
    );
  }
);

export default Menu;
